export function emptyGroupProductFields() {
    return {
        name: '',
        commercialName: '',
        url: '',
        price: 0,
        discount: 0,
        priceEnabled: false,
        installment: 1,
        shortDescription: '',
        longDescription: '',
        description: '',
        approvalEnabled: false,
        minGrade: 0,
        certificateEnabled: false,
        status: 'active',
        availabilityDeadline: 1,
        imageUrl: '',
        coverFile: null,
    };
}

function slugifyGroupUrl(value = '') {
    return String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80);
}

/**
 * Dados de demonstração para o formulário de pacote/grupo de cursos.
 */
export function buildDemoGroupProductFields(overrides = {}) {
    const stamp = Date.now().toString().slice(-5);
    const name = overrides.name || `Pacote demonstração ${stamp}`;
    const shortDescription = overrides.shortDescription
        || 'Pacote introdutório com cursos práticos para onboarding e desenvolvimento contínuo.';
    const longDescription = overrides.longDescription
        || 'Este pacote reúne conteúdos essenciais com aulas objetivas e atividades guiadas. Ideal para liberar um conjunto de cursos a um setor ou turma de uma vez.';

    return {
        ...emptyGroupProductFields(),
        name,
        commercialName: overrides.commercialName || name,
        url: overrides.url || slugifyGroupUrl(name),
        priceEnabled: true,
        price: 299.9,
        discount: 49.9,
        installment: 3,
        shortDescription,
        longDescription,
        description: shortDescription,
        approvalEnabled: true,
        minGrade: 7,
        certificateEnabled: true,
        status: 'active',
        availabilityDeadline: 90,
        ...overrides,
        // Garante nome/url/descrição coerentes mesmo com overrides parciais
        name: overrides.name || name,
        commercialName: overrides.commercialName || name,
        url: overrides.url || slugifyGroupUrl(overrides.name || name),
        shortDescription: overrides.shortDescription || shortDescription,
        longDescription: overrides.longDescription || longDescription,
        description: overrides.shortDescription || shortDescription,
    };
}

export function createDemoCoverFile(label = 'Pacote demo', filename = 'pacote-capa-demo.png') {
    return new Promise((resolve, reject) => {
        const width = 1080;
        const height = 400;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            reject(new Error('Canvas não disponível'));
            return;
        }

        const gradient = ctx.createLinearGradient(0, 0, width, height);
        gradient.addColorStop(0, '#0f3d5c');
        gradient.addColorStop(1, '#1f7a8c');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);

        ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
        ctx.font = `600 ${Math.max(28, Math.round(width / 18))}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(label).slice(0, 28), width / 2, height / 2);

        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Falha ao gerar imagem'));
                return;
            }
            resolve(
                new File([blob], filename, {
                    type: 'image/png',
                    lastModified: Date.now(),
                }),
            );
        }, 'image/png');
    });
}

export function mapGroupProductFromRow(row = {}) {
    const shortDescription = row.shortDescription || row.description || '';
    return {
        ...emptyGroupProductFields(),
        name: row.name || '',
        commercialName: row.commercialName || row.comercialName || '',
        url: row.url || '',
        price: Number(row.price) || 0,
        discount: Number(row.discount) || 0,
        priceEnabled:
            (Number(row.price) || 0) > 0 || (Number(row.discount) || 0) > 0,
        installment: Number(row.installment) || 1,
        shortDescription,
        longDescription: row.longDescription || '',
        description: shortDescription,
        approvalEnabled: row.approvalEnabled != null
            ? !!row.approvalEnabled
            : Number(row.approved) > 0,
        minGrade: row.minGrade != null
            ? Number(row.minGrade)
            : (Number(row.approved) > 0 ? Number(row.approved) : 0),
        certificateEnabled: row.certificateEnabled != null
            ? !!row.certificateEnabled
            : Number(row.certificate) === 1,
        status: row.status === 'inactive' || String(row.status) === '4' || String(row.status) === '0'
            ? 'inactive'
            : (row.status === 'internal' || String(row.status) === '2'
                ? 'internal'
                : (row.status === 'active' || String(row.status) === '1' || !row.status
                    ? 'active'
                    : row.status)),
        availabilityDeadline: Number(row.availabilityDeadline ?? row.dayExpired) || 1,
        imageUrl: row.imageFinalPath || row.imageUrl || '',
        coverFile: row.imageFinalPath || row.imageUrl || null,
    };
}

export function toGroupProductPayload(form = {}) {
    return {
        name: form.name || '',
        commercialName: form.commercialName || '',
        url: form.url || '',
        price: form.priceEnabled ? (Number(form.price) || 0) : 0,
        discount: form.priceEnabled ? (Number(form.discount) || 0) : 0,
        installment: form.priceEnabled ? (Number(form.installment) || 1) : 1,
        shortDescription: form.shortDescription || '',
        longDescription: form.longDescription || '',
        description: form.shortDescription || form.description || '',
        approvalEnabled: !!form.approvalEnabled,
        minGrade: form.approvalEnabled ? (Number(form.minGrade) || 0) : 0,
        approved: form.approvalEnabled ? (Number(form.minGrade) || 0) : -1,
        certificateEnabled: !!form.certificateEnabled,
        certificate: form.certificateEnabled ? 1 : 0,
        status: form.status === 'inactive'
            ? 'inactive'
            : (form.status === 'internal' || String(form.status) === '2' ? 'internal' : 'active'),
        availabilityDeadline: Number(form.availabilityDeadline) || 1,
        dayExpired: Number(form.availabilityDeadline) || 1,
        imageUrl: form.imageUrl || null,
    };
}
